import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Link,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  verifyOtp,
  forgetPassword,
} from "../../../services/User/Authentication";
import toast from "react-hot-toast";

const OtpModal = ({ open, handleClose, email }) => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^[0-9]$/.test(value)) {
      let newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (index < 5) inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      let newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      if (index > 0 && otp[index] === "") inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{1,6}$/.test(pastedData)) {
      const pastedArray = pastedData.split("");
      const newOtp = [...otp];
      pastedArray.forEach((char, i) => {
        if (i < 6) newOtp[i] = char;
      });
      setOtp(newOtp);
      const focusIndex = Math.min(pastedArray.length, 5);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
    }
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const payload = {
        email,
        otp: otp.join(""),
      };
      const res = await verifyOtp(payload);
      toast.success(res.message || "OTP Verified Successfully ✅");
      handleClose();

      navigate("/reset-password", {
        state: { email, token: res.reset_token },
      });
    } catch (err) {
      toast.error(err.error || "OTP Verification Failed ❌");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      const res = await forgetPassword({ email });
      toast.success(res.message || "OTP resent successfully ✅");
    } catch (err) {
      toast.error(err.error || "Failed to resend OTP ❌");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: "var(--radius-md)",
          bgcolor: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          color: "var(--color-text)",
          padding: 2,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 0 }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            backgroundColor: "rgba(200, 255, 0, 0.12)",
            color: "var(--color-primary)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
            fontSize: 24,
          }}
        >
          📩
        </Box>
        <h5 className="xl:text-[28px] md:text-2xl text-xl text-[var(--color-text)] mb-2">
          Check your email
        </h5>
      </DialogTitle>

      <DialogContent sx={{ textAlign: "center", mt: 2 }}>
        <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] mb-8">
          Enter the verification code sent to <br />
          <strong className="text-[var(--color-text)]">{email}</strong>
        </p>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 4 }}>
          {otp.map((data, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              maxLength={1}
              value={data}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              disabled={isVerifying}
              className="h-11 w-10 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg)] text-center text-xl font-bold text-[var(--color-text)] outline-none"
            />
          ))}
        </Box>

        <p className="flex justify-center items-center gap-1 mb-8 xl:text-base md:text-sm text-xs text-[var(--color-text-muted)]">
          Didn’t get a code?{" "}
          {isResending ? (
            <CircularProgress size={16} sx={{ color: "var(--color-primary)", ml: 1, verticalAlign: "middle" }} />
          ) : (
            <Link
              component="button"
              onClick={handleResend}
              sx={{ color: "var(--color-primary)", fontWeight: 800, textDecoration: "none" }}
            >
              Resend
            </Link>
          )}
        </p>

        <button
          onClick={handleVerify}
          disabled={isVerifying || otp.join("").length < 6}
          className={`w-full xl:text-base md:text-sm text-xs bg-[var(--color-primary)] text-black border-none p-3 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] ${isVerifying || otp.join("").length < 6 ? "cursor-not-allowed opacity-50" : "cursor-pointer opacity-100"}`}
        >
          {isVerifying ? "Verifying..." : "Verify email"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default OtpModal;
